import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Token storage helpers
const getStoredToken = () => localStorage.getItem('access_token');
const setStoredToken = (token) => localStorage.setItem('access_token', token);
const removeStoredToken = () => localStorage.removeItem('access_token');

// Create axios instance with interceptor
const api = axios.create({
    baseURL: API_URL,
    withCredentials: true
});

// Add token to all requests
api.interceptors.request.use((config) => {
    const token = getStoredToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Export for use in other components
export { api };

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        const token = getStoredToken();
        if (!token) {
            setUser(false);
            setLoading(false);
            return;
        }
        
        try {
            const response = await api.get('/api/auth/me');
            setUser(response.data);
        } catch (error) {
            setUser(false);
            removeStoredToken();
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        const response = await api.post('/api/auth/login', { email, password });
        // Store token in localStorage
        if (response.data.access_token) {
            setStoredToken(response.data.access_token);
        }
        setUser(response.data);
        return response.data;
    };

    const logout = async () => {
        try {
            await api.post('/api/auth/logout');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            removeStoredToken();
            setUser(false);
        }
    };

    const value = {
        user,
        loading,
        login,
        logout,
        isAuthenticated: !!user && user !== false,
        isAdmin: user?.role === 'admin'
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
